import { ReactNode, useState } from 'react';
import { WireframeSidebar } from './WireframeSidebar';
import { cn } from '../../lib/utils';
import { Menu } from 'lucide-react';

interface WireframeLayoutProps {
  children: ReactNode;
  className?: string;
  fullHeight?: boolean;
  noPadding?: boolean;
  embedded?: boolean;
}

export function WireframeLayout({ children, className, fullHeight, noPadding, embedded }: WireframeLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // In embedded mode, render with a compact always-visible sidebar
  if (embedded) {
    return (
      <div className={cn('flex bg-gray-50', fullHeight ? 'h-screen overflow-hidden' : 'min-h-screen')}>
        {/* Compact embedded sidebar - always visible */}
        <WireframeSidebar embedded />
        <main
          className={cn(
            'flex-1 flex flex-col min-w-0',
            fullHeight && 'h-full overflow-hidden',
            className
          )}
        >
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className={cn('flex bg-gray-50', fullHeight ? 'h-screen overflow-hidden' : 'min-h-screen')}>
      <WireframeSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <main
        className={cn(
          'flex-1 flex flex-col min-w-0',
          fullHeight && 'h-full overflow-hidden',
          className
        )}
      >
        {/* Mobile Header Bar */}
        <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => setSidebarOpen(true)}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div>
            <div className="text-lg font-bold text-accent-500">CLEAR</div>
            <div className="text-[10px] text-gray-500">Response Dashboard</div>
          </div>
        </div>
        {children}
      </main>
    </div>
  );
}

// Page Header Component
interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface WireframeHeaderProps {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
}

export function WireframeHeader({ title, subtitle, actions, breadcrumbs }: WireframeHeaderProps) {
  return (
    <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex items-center gap-1.5 text-xs mb-2">
          {breadcrumbs.map((item, index) => (
            <span key={index} className="flex items-center gap-1.5">
              {index > 0 && <span className="text-gray-300">/</span>}
              {item.href ? (
                <a href={item.href} className="text-gray-500 hover:text-accent-500">
                  {item.label}
                </a>
              ) : (
                <span className="text-gray-500">{item.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold text-gray-900">{title}</h1>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>
    </header>
  );
}

// Content Area Component
interface WireframeContentProps {
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export function WireframeContent({ children, className, noPadding }: WireframeContentProps) {
  return (
    <div className={cn('flex-1 overflow-y-auto', !noPadding && 'p-4 sm:p-6', className)}>
      {children}
    </div>
  );
}

// Tabs Component
interface WireframeTab {
  label: string;
  active?: boolean;
  onClick?: () => void;
}

interface WireframeTabsProps {
  tabs: WireframeTab[];
  onTabClick?: (index: number) => void;
}

export function WireframeTabs({ tabs, onTabClick }: WireframeTabsProps) {
  return (
    <div className="flex border-b border-gray-200 mb-5 overflow-x-auto">
      {tabs.map((tab, index) => (
        <button
          key={tab.label}
          onClick={() => {
            tab.onClick?.();
            onTabClick?.(index);
          }}
          className={cn(
            'px-4 sm:px-5 py-3 text-[13px] font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
            tab.active
              ? 'text-accent-500 border-accent-500'
              : 'text-gray-500 border-transparent hover:text-gray-900'
          )}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// Stats Grid Component
interface StatCardProps {
  label: string;
  value: string | number;
  valueColor?: string;
  change?: { value: string; direction: 'up' | 'down' };
  icon?: ReactNode;
}

export function StatCard({ label, value, valueColor, change, icon }: StatCardProps) {
  return (
    <div className="bg-white border border-gray-200 p-4 sm:p-5">
      {icon && (
        <div className="flex items-center gap-2 mb-2">
          <span className="text-gray-400">{icon}</span>
          <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-0">
            {label}
          </span>
        </div>
      )}
      {!icon && (
        <div className="text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-2">
          {label}
        </div>
      )}
      <div className={cn('text-2xl sm:text-[28px] font-bold leading-none', valueColor || 'text-gray-900')}>
        {value}
      </div>
      {change && (
        <div
          className={cn(
            'text-xs mt-2 flex items-center gap-1',
            change.direction === 'up' ? 'text-green-600' : 'text-red-600'
          )}
        >
          {change.direction === 'up' ? '↑' : '↓'} {change.value}
        </div>
      )}
    </div>
  );
}

// Card Component
interface WireframeCardProps {
  title?: string | ReactNode;
  subtitle?: string;
  headerAction?: ReactNode;
  children: ReactNode;
  className?: string;
  noPadding?: boolean;
}

export function WireframeCard({
  title,
  subtitle,
  headerAction,
  children,
  className,
  noPadding,
}: WireframeCardProps) {
  return (
    <div className={cn('bg-white border border-gray-200', className)}>
      {(title || headerAction) && (
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            {title && <div className="text-sm font-semibold text-gray-900">{title}</div>}
            {subtitle && <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>}
          </div>
          {headerAction}
        </div>
      )}
      <div className={cn(!noPadding && 'p-4 sm:p-5')}>{children}</div>
    </div>
  );
}

// Button Component
interface WireframeButtonProps {
  children: ReactNode;
  variant?: 'default' | 'primary';
  size?: 'sm' | 'md';
  icon?: ReactNode;
  onClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export function WireframeButton({
  children,
  variant = 'default',
  size = 'md',
  icon,
  onClick,
  className,
  disabled = false,
}: WireframeButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-1.5 font-medium transition-colors',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3 py-2 text-[13px]',
        variant === 'primary'
          ? 'bg-accent-500 text-white border border-accent-500 hover:bg-accent-600'
          : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-white hover:border-gray-300',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
    >
      {icon}
      {children}
    </button>
  );
}

// Table Component
interface WireframeTableColumn {
  key: string;
  label: string;
  width?: string;
  hideOnMobile?: boolean;
}

interface WireframeTableProps {
  columns: WireframeTableColumn[];
  data: Record<string, ReactNode>[];
}

export function WireframeTable({ columns, data }: WireframeTableProps) {
  return (
    <div className="overflow-x-auto -mx-4 sm:mx-0">
      <table className="w-full border-collapse min-w-[600px]">
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cn(
                  "px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wide bg-gray-50 border-b border-gray-200",
                  col.hideOnMobile && "hidden sm:table-cell"
                )}
                style={{ width: col.width }}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index} className="hover:bg-gray-50">
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={cn(
                    "px-4 py-3 text-[13px] text-gray-600 border-b border-gray-200",
                    col.hideOnMobile && "hidden sm:table-cell"
                  )}
                >
                  {row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Status Indicator
interface StatusIndicatorProps {
  status: 'online' | 'offline' | 'delayed';
  label?: string;
}

export function StatusIndicator({ status, label }: StatusIndicatorProps) {
  const colors = {
    online: 'bg-green-600',
    offline: 'bg-red-600',
    delayed: 'bg-amber-500',
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={cn('w-2 h-2', colors[status])} />
      {label && <span>{label}</span>}
    </span>
  );
}

// Crisis Badge
interface CrisisBadgeProps {
  severity: 'critical' | 'high' | 'medium' | 'low';
}

export function CrisisBadge({ severity }: CrisisBadgeProps) {
  const styles = {
    critical: 'bg-red-100 text-red-600',
    high: 'bg-amber-100 text-amber-600',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-green-100 text-green-600',
  };

  return (
    <span
      className={cn(
        'text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5',
        styles[severity]
      )}
    >
      {severity}
    </span>
  );
}

export default WireframeLayout;
